/*!
 * CFIR home
 * Copyright © 2026 Canadian Foundation for Innovation and Research.
 * All rights reserved. See /LICENSE.
 */

/* ─────────────────────────────────────────────────────────────
   One content set, two skins:

   2D    — the DEFAULT. Static, crawlable HTML already in
           index.html (#home2d). Nothing to boot: zero JS work.
           What Google indexes, what a first-time visitor gets.
   flat  — the gov.uk-strict accessible view. Explicit choice,
           or the standing gate: prefers-reduced-motion lands
           here automatically. Phones get the standard view.

   The immersive experience is no longer a WebGL tunnel bolted onto
   this page — it is its own scroll scene at /mission/. Any old
   'immersive' intent redirects there.
   ───────────────────────────────────────────────────────────── */
type View = 'standard' | 'accessible'

function resolveView(): View {
  // URL intent (?view=) is an explicit, conscious choice: persist it so the
  // whole site stays in that view as the user navigates, then strip the param.
  let param: string | null = null
  try {
    param = new URLSearchParams(location.search).get('view')
    if (param === 'accessible' || param === 'standard') {
      localStorage.setItem('cfir-view', param)
      history.replaceState(null, '', location.pathname + location.hash)
    }
  } catch { /* private mode / no history API */ }

  let stored: string | null = null
  try { stored = localStorage.getItem('cfir-view') } catch { /* private mode */ }

  // The old 3D tunnel is retired — the immersive scene now lives at /mission/.
  // A cached ?view=immersive link or a stored 'immersive' view sends the
  // visitor to the new scene instead of a dead branch.
  if (param === 'immersive' || stored === 'immersive') {
    try { localStorage.removeItem('cfir-view') } catch { /* private mode */ }
    location.replace('/mission/')
    return 'standard'
  }

  if (stored === 'accessible') return 'accessible'
  if (stored === 'standard') return 'standard'
  // The standing accessibility gate: reduced-motion and small screens get the
  // maximum-legibility view without asking.
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return 'accessible'
  return 'standard'
}

function bootAccessible(): void {
  void (async () => {
    const [{ FlatView }, { EN }, { FR }] = await Promise.all([
      import('./flat/FlatView'),
      import('./content/en'),
      import('./content/fr'),
    ])
    new FlatView(EN, FR)
    document.body.classList.add('site-ready')
  })()
}

function bootStandard(): void {
  // The standard home is fully self-contained in the HTML — its own header,
  // sections, footer and inline scripts (accordion / carousel / newsletter).
  document.body.classList.add('site-ready')
}

// Light is the only palette across every view — there is no dark theme.
document.body.classList.add('light')

const view = resolveView()
if (view === 'accessible') bootAccessible()
else bootStandard()
